import React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import FlaggableHierarchyTreeItemReadOnly from "./flaggable-hierarchy-tree-item-read-only";
import FlaggableHierarchyTreeItem from "./flaggable-hierarchy-tree-item";
import "../hierarchy-tree/hierarchy-tree.style.scss";
import "./flaggable-hierarchy-tree.style.scss";

class FlaggableHierarchyTree extends React.Component {
  findNode = (currentTree, id) => {
    return currentTree && currentTree.data.id === id
      ? currentTree
      : currentTree.children.reduce((acc, branch) => acc || this.findNode(branch, id), null);
  };

  findAllChildrenIds = node => {
    return (
      node &&
      node.children.reduce(
        (acc, branch) => acc.concat([branch.data.id].concat(this.findAllChildrenIds(branch))),
        [],
      )
    );
  };

  onClickHandler = event => {
    event && event.stopPropagation();
    let {
      id,
      dataset: { isSelected },
    } = event.currentTarget;
    id = +id;
    isSelected = isSelected !== "false";
    let { excluded, selected } = this.props;
    if (isSelected) {
      const selectedWithoutElement = selected.filter(el => el.id !== id);
      if (selectedWithoutElement.length < selected.length) {
        // case of element being previously selected directly
        selected = selectedWithoutElement;
        const childrenIds = this.findAllChildrenIds(this.findNode(this.props.tree, id))
          // converting the list with all the ids into a hash to improve lookup performances
          .reduce((acc, id) => Object.assign(acc, { [id]: true }), {});
        //filtering out all the elements of excluded which were descendants of id
        excluded = excluded.filter(el => !childrenIds[el.id]);
      } else {
        // case of element being already selected through one of its ancestors
        excluded.push({ id });
      }
    } else {
      const excludedWithoutElement = excluded.filter(el => el.id !== id);
      // base case - element not selected as it was explicitly excluded
      if (excludedWithoutElement.length < excluded.length) {
        excluded = excludedWithoutElement;
      } else {
        selected.push({ id });
      }
      // removing all the children ids from selected, to avoid duplications
      const childrenIds = this.findAllChildrenIds(this.findNode(this.props.tree, id))
        // converting the list with all the ids into a hash to improve lookup performances
        .reduce((acc, id) => Object.assign(acc, { [id]: true }), {});
      selected = selected.filter(el => !childrenIds[el.id]);
    }
    this.props.onChange({
      excluded,
      selected,
    });
  };

  parseTree(
    tree,
    layer = 0,
    isSelected = Boolean(this.props.selected.find(el => el.id === tree.data.id)),
    path = [],
  ) {
    return (
      <div className="w-100" key={tree.data.id}>
        {this.props.readOnly ? (
          <FlaggableHierarchyTreeItemReadOnly
            indentSize={this.props.indentSize}
            layer={layer}
            label={tree.data.label}
            isSelected={isSelected}
          />
        ) : (
          <FlaggableHierarchyTreeItem
            checkboxClassName={this.props.checkboxClassName}
            id={tree.data.id}
            indentSize={this.props.indentSize}
            label={tree.data.label}
            layer={layer}
            onClick={this.onClickHandler}
            isSelected={isSelected}
          />
        )}
        {tree.children.map(child =>
          this.parseTree(
            child,
            layer + 1,
            (isSelected || this.props.selected.find(el => el.id === child.data.id)) &&
              !this.props.excluded.find(el => el.id === child.data.id),
            path.concat(tree.data.id),
          ),
        )}
      </div>
    );
  }

  render() {
    return (
      <div className={classNames("flaggable-hierarchy-tree-default w-100", this.props.className)}>
        {this.parseTree(this.props.tree)}
      </div>
    );
  }
}

FlaggableHierarchyTree.propTypes = {
  checkboxClassName: PropTypes.object,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.object,
  ]),
  excluded: PropTypes.array,
  indentSize: PropTypes.number,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  selected: PropTypes.array,
  tree: PropTypes.object,
};

FlaggableHierarchyTree.defaultProps = {
  excluded: [],
  indentSize: 20,
  selected: [],
};

export default FlaggableHierarchyTree;
